import type { OnboardingResearchProfile } from '@bower/shared';

/**
 * The wizard's resumable draft. Everything here is either not yet on the
 * server (`stepKey`, an un-submitted `workspaceName`, research taps before
 * Continue) or cheaper to read locally than to re-derive.
 *
 * `version` is checked on read: a shape change ships as a bump, and an
 * older/unknown record is DISCARDED rather than partially trusted. Silently
 * half-applying a stale draft is the failure this exists to prevent.
 */
export const ONBOARDING_DRAFT_VERSION = 1;

/**
 * How long an abandoned draft may sit on the device. It holds free-text
 * answers (`fieldOther` / `roleOther`) and a workspace name that is routinely a
 * surname, and completion is the only thing that clears it — so without a bound
 * an abandoned onboarding keeps them indefinitely on a shared lab machine.
 * Long enough that a genuine "come back after the weekend" resume still works.
 */
export const ONBOARDING_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface OnboardingDraft {
  readonly version: number;
  readonly stepKey?: string;
  readonly workspaceName?: string;
  readonly research?: OnboardingResearchProfile;
  /** Epoch ms of the last write. Absent on drafts written before the TTL landed. */
  readonly savedAt?: number;
}

function asOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * Total: a corrupt record, an unknown version, a stale one, or disabled storage
 * all read as "no draft".
 *
 * Every field is NARROWED rather than cast. A blanket `as OnboardingDraft` is
 * what let a hand-edited `{"workspaceName": 42}` reach `workspaceName.trim()`
 * inside an async submit handler — outside its `try`, so an unhandled rejection
 * with no toast and a silently dead Continue button.
 */
export function readOnboardingDraft(key: string | null): OnboardingDraft | null {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;

    const record = parsed as Record<string, unknown>;
    if (record.version !== ONBOARDING_DRAFT_VERSION) return null;

    const savedAt = typeof record.savedAt === 'number' ? record.savedAt : undefined;
    if (savedAt !== undefined && Date.now() - savedAt > ONBOARDING_DRAFT_TTL_MS) {
      clearOnboardingDraft(key);
      return null;
    }

    const research =
      typeof record.research === 'object' && record.research !== null
        ? (record.research as OnboardingResearchProfile)
        : undefined;

    return {
      version: ONBOARDING_DRAFT_VERSION,
      stepKey: asOptionalString(record.stepKey),
      workspaceName: asOptionalString(record.workspaceName),
      research,
      savedAt,
    };
  } catch {
    return null;
  }
}

/**
 * Merge a patch into the stored draft.
 *
 * Merging rather than replacing is the contract: the step, the workspace name
 * and the research answers are written from three independent call sites, and a
 * replacing writer would make each one silently clear the other two.
 */
export function writeOnboardingDraft(
  key: string | null,
  patch: Partial<Omit<OnboardingDraft, 'version'>>,
): void {
  if (!key) return;
  try {
    const next: OnboardingDraft = {
      ...(readOnboardingDraft(key) ?? {}),
      ...patch,
      version: ONBOARDING_DRAFT_VERSION,
      savedAt: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* storage full / disabled — non-fatal, resume degrades to the server floor */
  }
}

/**
 * Every clear in this module is the same three lines around one `removeItem`.
 * Shared so a third copy doesn't have to re-derive that a disabled Storage
 * throws on access, not just on write.
 */
function removeStoredKey(storage: Storage, key: string | null): void {
  if (!key) return;
  try {
    storage.removeItem(key);
  } catch {
    /* storage disabled — non-fatal */
  }
}

export function clearOnboardingDraft(key: string | null): void {
  removeStoredKey(localStorage, key);
}

/**
 * Drop EVERY onboarding draft, whoever wrote it.
 *
 * Clearing only the current key is not enough on two paths. The key is derived
 * from `internalId || uid`, so a boot where `/auth/login` fails resolves a
 * different identity and orphans the earlier record under a key nothing will
 * ever revisit. And on sign-out there is no "current key" at all — yet that is
 * exactly when a shared machine most needs the free text gone.
 */
export function clearAllOnboardingDrafts(prefix: string): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) doomed.push(key);
    }
    // Collected first: removing during the walk reindexes it and skips entries.
    doomed.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* storage disabled — non-fatal */
  }
}

export function readResumeWorkspaceId(key: string | null): string | null {
  if (!key) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function setResumeWorkspaceId(key: string | null, id: string): void {
  if (!key) return;
  try {
    localStorage.setItem(key, id);
  } catch {
    /* storage disabled — non-fatal */
  }
}

export function clearResumeWorkspaceId(key: string | null): void {
  removeStoredKey(localStorage, key);
}

export function alreadyTrackedStart(key: string | null): boolean {
  if (!key) return false;
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function markStartTracked(key: string | null): void {
  if (!key) return;
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    /* storage full / disabled — non-fatal, guard degrades to per-mount */
  }
}

export function readOrcidDraft(key: string | null): string | null {
  if (!key) return null;
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeOrcidDraft(key: string | null, value: string): void {
  if (!key) return;
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* storage full / disabled — non-fatal */
  }
}

export function clearOrcidDraft(key: string | null): void {
  removeStoredKey(sessionStorage, key);
}
